﻿import { jsPDF } from "jspdf";
import {
  mmToPt, drawHeader, drawFooter, ensureSpace, getTextRegion, getSignatureLineY, addPageWithHeader,
} from "./dischargeSummaryLayout";
import { fmtDatePdf, fmtDateTime } from "@emr/shared/india";
import { computeAgeYm, formatAgeLabel } from "@emr/shared/age";
import { groupTaperSteps } from "@emr/shared";
import type { TaperStep } from "@emr/shared";
import type { DischargeHeading } from "@emr/shared/constants";

// ----------------------------------------------------------------------------
// Independent renderer (house convention): inlined private copies of the
// shared helpers — editing this file must never require touching
// prescriptionPdf.ts / visitSummaryPdf.ts (and vice versa).
// ----------------------------------------------------------------------------

// ── Table ruling (2026-10-26; unified same day) ──────────────────────────
// ALL table ruling — horizontal row dividers, outer frames, AND vertical
// column separators — prints at 0.5pt warm gray (150,138,128), mirroring
// prescriptionPdf.ts. (The original 0.3pt #E1DAD2 ruling was effectively
// invisible on paper; an interim two-weight scheme with subtle verticals
// was unified to this single visible weight per user request.)
const RULE_COLOR: [number, number, number] = [150, 138, 128];
const RULE_WIDTH = 0.5;

export interface DischargeMedicinePdf {
  serialNo: number;
  name: string;
  composition: string | null;
  strength: string | null;
  frequency: string | null;
  taper: TaperStep[] | null;
  duration: string | null;
  instructions: string | null;
}

// Typography spec (2026-10-16): document title 16 bold underlined; section
// headings 12 bold; body text 11; double spacing (2x font size) throughout.
const TITLE_FONT = 16;
const HEADING_FONT = 12;
const BODY_FONT = 11;
const BODY_LINE_H = BODY_FONT * 2; // 22pt — double spacing

export interface DischargeSummaryPdfData {
  heading: DischargeHeading;
  patientName: string;
  clinicUhid: string;
  patientDob: string | null;
  patientGender: string | null;
  patientPhone: string | null;
  patientAddress: string | null;
  admissionAt: string | null;
  dischargeAt: string | null;
  diagnosis: string | null;
  history: string | null;
  examination: string | null;
  investigations: string | null;
  treatmentInHospital: string | null;
  courseInHospital: string | null;
  generalAdvice: string | null;
  reviewDate: string | null;
  reviewText: string | null;
  showPrescription: boolean;
  showReviewDate: boolean;
  medicines: DischargeMedicinePdf[];
  showComposition: boolean;
  doctorName: string | null;
  doctorQualifications: string | null;
  doctorRegNumber: string | null;
}

/**
 * Patient identity rows (private inlined copy — prescriptionPdf style).
 * Row 1: "Name: {name}" (left) · "UHID: {uhid}" (right);
 * Row 2: "Age: {age}    Sex: {sex}" (left) · "Mobile: {phone}" (right);
 * Row 3: "Address: {address}" (full width, wraps);
 * Row 4: "Date of Admission: {dt}" (left) · "Date of Discharge: {dt}" (right).
 * Body font (11) with double spacing. Returns the Y one line-height below
 * the last row's baseline.
 */
function drawPatientIdentityRows(
  doc: jsPDF,
  data: {
    patientName: string;
    clinicUhid: string;
    patientDob: string | null;
    patientGender: string | null;
    patientPhone: string | null;
    patientAddress: string | null;
    admissionAt: string | null;
    dischargeAt: string | null;
  },
  startY: number,
  region: { x: number; width: number },
): number {
  let y = startY;
  const x = region.x;
  const w = region.width;
  const lineH = BODY_LINE_H;
  const midX = x + w / 2;
  const rightX = x + w;
  const halfGap = mmToPt(6);

  const age = formatAgeLabel(computeAgeYm(data.patientDob));
  doc.setTextColor(26, 20, 19);

  // Row 1 — Name (left), UHID (right).
  const r1l = drawMixedLine(doc, [
    { text: "Name: ", bold: true },
    { text: data.patientName, bold: false },
  ], x, y, midX - halfGap, lineH, BODY_FONT);
  const r1r = drawMixedLine(doc, [
    { text: "UHID: ", bold: true },
    { text: data.clinicUhid, bold: false },
  ], midX + halfGap, y, rightX, lineH, BODY_FONT, "right");
  y = Math.max(r1l, r1r) + lineH;

  // Row 2 — Age (tab) Sex (left), Mobile (right).
  const ageSexSegments: { text: string; bold: boolean }[] = [];
  if (age) ageSexSegments.push({ text: "Age: ", bold: true }, { text: age, bold: false });
  if (data.patientGender) {
    if (ageSexSegments.length > 0) ageSexSegments.push({ text: "    ", bold: false });
    ageSexSegments.push({ text: "Sex: ", bold: true }, { text: data.patientGender, bold: false });
  }
  const r2l = drawMixedLine(doc, ageSexSegments, x, y, midX - halfGap, lineH, BODY_FONT);
  const r2r = drawMixedLine(doc, [
    { text: "Mobile: ", bold: true },
    { text: data.patientPhone ?? "—", bold: false },
  ], midX + halfGap, y, rightX, lineH, BODY_FONT, "right");
  y = Math.max(r2l, r2r) + lineH;

  // Row 3 — Address (full width, wraps to multiple lines).
  const r3 = drawMixedLine(doc, [
    { text: "Address: ", bold: true },
    { text: data.patientAddress?.trim() || "—", bold: false },
  ], x, y, rightX, lineH, BODY_FONT);
  y = r3 + lineH;

  // Row 4 — Date of Admission (left), Date of Discharge (right).
  const r4l = drawMixedLine(doc, [
    { text: "Date of Admission: ", bold: true },
    { text: data.admissionAt ? fmtDateTime(data.admissionAt) : "—", bold: false },
  ], x, y, midX - halfGap, lineH, BODY_FONT);
  const r4r = drawMixedLine(doc, [
    { text: "Date of Discharge: ", bold: true },
    { text: data.dischargeAt ? fmtDateTime(data.dischargeAt) : "—", bold: false },
  ], midX + halfGap, y, rightX, lineH, BODY_FONT, "right");
  y = Math.max(r4l, r4r) + lineH;

  return y;
}

/**
 * Draw a single logical line made of mixed bold/normal segments, wrapping at
 * word boundaries within the given box (private inlined copy). align "left"
 * starts at x and wraps toward maxX; align "right" is right-justified to maxX
 * and wraps toward it. Returns the baseline Y of the last drawn line.
 */
function drawMixedLine(
  doc: jsPDF,
  segments: { text: string; bold: boolean }[],
  x: number,
  y: number,
  maxX: number,
  lineH: number,
  size: number,
  align: "left" | "right" = "left",
): number {
  doc.setFontSize(size);
  const words: { text: string; bold: boolean; width: number }[] = [];
  for (const seg of segments) {
    doc.setFont("helvetica", seg.bold ? "bold" : "normal");
    for (const w of seg.text.split(/(\s+)/)) {
      if (w === "") continue;
      words.push({ text: w, bold: seg.bold, width: doc.getTextWidth(w) });
    }
  }
  const right = align === "right";
  const avail = maxX - x;
  const lines: { text: string; bold: boolean; width: number }[][] = [];
  let cur: typeof words = [];
  let cw = 0;
  for (const word of words) {
    if (word.text.trim() === "") {
      if (cur.length === 0) continue;
      cur.push(word);
      cw += word.width;
      continue;
    }
    if (cur.length > 0 && cw + word.width > avail) {
      lines.push(cur);
      cur = [word];
      cw = word.width;
    } else {
      cur.push(word);
      cw += word.width;
    }
  }
  if (cur.length > 0) lines.push(cur);

  let cy = y;
  for (const line of lines) {
    const l = line[line.length - 1]?.text.trim() === "" ? line.slice(0, -1) : line;
    const lw = l.reduce((s, wd) => s + wd.width, 0);
    let cx = right ? maxX - lw : x;
    for (const word of l) {
      if (word.text.trim() === "") {
        cx += word.width;
        continue;
      }
      doc.setFont("helvetica", word.bold ? "bold" : "normal");
      doc.text(word.text, cx, cy);
      cx += word.width;
    }
    cy += lineH;
  }
  return cy - lineH;
}

/**
 * Split a diagnosis textarea into numbered print items (private inlined
 * copy) — each non-empty line is an item; a user-typed "1." / "1)" / "1:"
 * prefix is dropped (the PDF applies its own numbering).
 */
function splitDiagnosisItems(diagnosis: string | null): string[] {
  if (!diagnosis) return [];
  return diagnosis
    .split(/\r?\n/)
    .map((line) => line.trim().replace(/^\d+[.)\]:]\s*/, ""))
    .filter((line) => line.length > 0);
}

/**
 * Numbered diagnosis items in a single-column list, one below the other
 * (private inlined copy of the prescription info-block diagnosis table,
 * reworked 2026-10-16 to a single full-width column). Returns the Y below
 * the table.
 */
function drawDiagnosisList(
  doc: jsPDF,
  diagnosis: string | null,
  x: number,
  w: number,
  startY: number,
): number {
  const y = startY;
  const items = splitDiagnosisItems(diagnosis);
  if (items.length === 0) return y;

  const diagInset = mmToPt(5);
  const dx = x + diagInset;
  const dw = w - 2 * diagInset;
  const diagTextInset = mmToPt(2);
  const diagTextWrap = dw - diagTextInset - mmToPt(2);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(BODY_FONT);
  doc.setTextColor(60, 50, 45);

  const diagBase = mmToPt(4.2);
  const diagLineH = BODY_LINE_H;
  const rowHeights: number[] = [];
  for (let i = 0; i < items.length; i++) {
    const lines = doc.splitTextToSize(`${i + 1}. ${items[i]}`, diagTextWrap);
    rowHeights.push(diagBase + diagLineH * (lines.length - 1) + mmToPt(2.1));
  }
  const tableTop = y;
  let rowY = y;
  for (let i = 0; i < items.length; i++) {
    if (i % 2 === 1) {
      doc.setFillColor(252, 248, 243);
      doc.rect(dx, rowY, dw, rowHeights[i], "F");
    }
    const lines = doc.splitTextToSize(`${i + 1}. ${items[i]}`, diagTextWrap);
    if (lines.length > 0) doc.text(lines, dx + diagTextInset, rowY + diagBase);
    rowY += rowHeights[i];
  }
  const tableBottom = rowY;

  doc.setDrawColor(...RULE_COLOR);
  doc.setLineWidth(RULE_WIDTH);
  doc.rect(dx, tableTop, dw, tableBottom - tableTop);
  let sepY = tableTop;
  for (let i = 0; i < items.length - 1; i++) {
    sepY += rowHeights[i];
    doc.line(dx, sepY, dx + dw, sepY);
  }

  return tableBottom;
}

export async function createDischargeSummaryPdf(data: DischargeSummaryPdfData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);

  await drawHeader(doc);
  await drawFooter(doc);

  // Body region — full text width (no prescription-style left inset; this is
  // a document, not a pad).
  const baseRegion = getTextRegion(doc);
  const x = baseRegion.x;
  const w = baseRegion.width;
  let y = baseRegion.y;

  // ---- Document heading (centered, maroon, 16 bold underlined) ----
  const headingText = data.heading.toUpperCase();
  y += mmToPt(5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TITLE_FONT);
  doc.setTextColor(124, 45, 18);
  doc.text(headingText, x + w / 2, y, { align: "center" });
  const headingWidth = doc.getTextWidth(headingText);
  doc.setDrawColor(124, 45, 18);
  doc.setLineWidth(0.75);
  doc.line(x + w / 2 - headingWidth / 2, y + 1.5, x + w / 2 + headingWidth / 2, y + 1.5);
  y += mmToPt(8);

  // ---- Patient identity rows ----
  // Row 1: Name (left) · UHID (right)
  // Row 2: Age (tab) Sex (left) · Mobile (right)
  // Row 3: Date of Admission (left) · Date of Discharge (right)
  y = drawPatientIdentityRows(doc, {
    patientName: data.patientName,
    clinicUhid: data.clinicUhid,
    patientDob: data.patientDob,
    patientGender: data.patientGender,
    patientPhone: data.patientPhone,
    patientAddress: data.patientAddress,
    admissionAt: data.admissionAt,
    dischargeAt: data.dischargeAt,
  }, y, { x, width: w });

  y += mmToPt(5);

  // ---- Diagnosis (heading 12 bold; items single-column, one per row) ----
  y = await ensureSpace(doc, y, mmToPt(14));
  doc.setFont("helvetica", "bold");
  doc.setFontSize(HEADING_FONT);
  doc.setTextColor(124, 45, 18);
  doc.text("Diagnosis", x, y);
  y += mmToPt(5);
  y = drawDiagnosisList(doc, data.diagnosis, x, w, y + mmToPt(2));
  y += mmToPt(5);

  // ---- Narrative sections (visit-summary style; skipped when empty) ----
  const sections: [string, string | null][] = [
    ["History", data.history],
    ["Examination", data.examination],
    ["Investigations", data.investigations],
    ["Treatment in Hospital", data.treatmentInHospital],
    ["Course in Hospital", data.courseInHospital],
  ];

  for (const [label, content] of sections) {
    if (!content || !content.trim()) continue; // skip empty sections entirely
    y = await ensureSpace(doc, y, mmToPt(14));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(HEADING_FONT);
    doc.setTextColor(124, 45, 18);
    doc.text(label, x, y);
    y += mmToPt(5);
    y += mmToPt(2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(BODY_FONT);
    doc.setTextColor(26, 20, 19);
    const lines = doc.splitTextToSize(content, w);
    for (const line of lines) {
      y = await ensureSpace(doc, y, BODY_LINE_H);
      doc.text(line, x, y);
      y += BODY_LINE_H;
    }
    y += mmToPt(3);
  }

  // ---- Advice on Discharge ----
  y = await ensureSpace(doc, y, mmToPt(14));
  doc.setFont("helvetica", "bold");
  doc.setFontSize(HEADING_FONT);
  doc.setTextColor(124, 45, 18);
  doc.text("Advice on Discharge", x, y);
  y += mmToPt(5);

  if (data.generalAdvice && data.generalAdvice.trim()) {
    y += mmToPt(2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(BODY_FONT);
    doc.setTextColor(26, 20, 19);
    const adviceLines = doc.splitTextToSize(data.generalAdvice, w);
    for (const line of adviceLines) {
      y = await ensureSpace(doc, y, BODY_LINE_H);
      doc.text(line, x, y);
      y += BODY_LINE_H;
    }
  }

  // ---- Prescription table (identical rendering to the EMR prescription
  // print — same columns, taper sub-rows, composition toggle, pagination) —
  // ONLY when showPrescription and medicines exist. ----
  if (data.showPrescription && data.medicines.length > 0) {
    y += mmToPt(6);
    y = await ensureSpace(doc, y, mmToPt(20));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(HEADING_FONT);
    doc.setTextColor(124, 45, 18);
    doc.text("Prescription", x, y);
    y += mmToPt(7);

    y = await drawMedicineTable(doc, data.medicines, data.showComposition, x, w, y);
  }

  // ---- Review text + signature block (2026-10-16) ----
  // The review sentence prints immediately below the prescription; the
  // signature follows 3 double-spaced rows below the review text. The whole
  // unit (review lines + signature block) is kept together on one page:
  // if the full 3-row gap doesn't fit, the gap compresses so the signature
  // falls back into the current page; only when even the minimum gap can't
  // fit does the unit move to a fresh page (signature never orphaned).
  const signX = x + w - mmToPt(60);
  const SIGN_BLOCK_H = mmToPt(12); // rule + name + quals + reg lines below it
  const SIGN_GAP_ROWS = 3; // double-spaced rows between review text and rule
  const SIGN_GAP_MIN_ROWS = 1; // compression floor — never less than 1 row

  if (data.showReviewDate) {
    // Compose the review text: the stored editable sentence, or the
    // date-derived fallback when only a date is set.
    const reviewBody = (data.reviewText && data.reviewText.trim())
      ? data.reviewText.trim()
      : (data.reviewDate
        ? `Please review in PS Neurocentre on ${fmtDatePdf(data.reviewDate)}. In case of any emergency please contact your local doctor or during working hours please call 88037 88027 or 0462-4220087`
        : "Review if symptoms persist or worsen");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(BODY_FONT);
    doc.setTextColor(26, 20, 19);
    const reviewLines = doc.splitTextToSize(reviewBody, w) as string[];
    const reviewH = reviewLines.length * BODY_LINE_H;

    // Fit logic: try the full 3-row gap; compress toward the 1-row floor;
    // move to a fresh page only when even the floor can't fit.
    const region = getTextRegion(doc);
    const bottomLimit = region.y + region.maxHeight;
    const available = bottomLimit - y;
    const minUnitH = reviewH + SIGN_GAP_MIN_ROWS * BODY_LINE_H + SIGN_BLOCK_H;
    if (available < minUnitH) {
      y = await addPageWithHeader(doc);
    }
    const region2 = getTextRegion(doc);
    const avail2 = region2.y + region2.maxHeight - y;
    const gapH = Math.max(
      SIGN_GAP_MIN_ROWS * BODY_LINE_H,
      Math.min(SIGN_GAP_ROWS * BODY_LINE_H, avail2 - reviewH - SIGN_BLOCK_H),
    );

    for (const line of reviewLines) {
      y = await ensureSpace(doc, y, BODY_LINE_H);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(BODY_FONT);
      doc.setTextColor(26, 20, 19);
      doc.text(line, x, y);
      y += BODY_LINE_H;
    }

    // Signature rule below the review text (full 3-row gap, or the
    // compressed gap when the page was tight).
    const signY = y + gapH - BODY_LINE_H;
    doc.setDrawColor(200, 190, 180);
    doc.line(signX, signY, signX + mmToPt(50), signY);
    doc.setFontSize(BODY_FONT);
    doc.setFont("helvetica", "bold");
    doc.text(data.doctorName ?? "Doctor", signX + mmToPt(50), signY + mmToPt(4), { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(107, 95, 88);
    if (data.doctorQualifications) {
      doc.text(data.doctorQualifications, signX + mmToPt(50), signY + mmToPt(7), { align: "right" });
    }
    if (data.doctorRegNumber) {
      doc.text(`Reg No: ${data.doctorRegNumber}`, signX + mmToPt(50), signY + mmToPt(10), { align: "right" });
    }
  } else {
    // Review hidden — signature still prints, anchored above the footer.
    const signY = getSignatureLineY(doc);
    doc.setDrawColor(200, 190, 180);
    doc.line(signX, signY, signX + mmToPt(50), signY);
    doc.setFontSize(BODY_FONT);
    doc.setFont("helvetica", "bold");
    doc.text(data.doctorName ?? "Doctor", signX + mmToPt(50), signY + mmToPt(4), { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(107, 95, 88);
    if (data.doctorQualifications) {
      doc.text(data.doctorQualifications, signX + mmToPt(50), signY + mmToPt(7), { align: "right" });
    }
    if (data.doctorRegNumber) {
      doc.text(`Reg No: ${data.doctorRegNumber}`, signX + mmToPt(50), signY + mmToPt(10), { align: "right" });
    }
  }

  return doc;
}

// ----------------------------------------------------------------------------
// Medicine table — private inlined copy of the prescriptionPdf table
// machinery (columns, taper groups, composition subtitle, zebra rows,
// repeated headers, no mid-row page splits).
// ----------------------------------------------------------------------------

async function drawMedicineTable(
  doc: jsPDF,
  medicines: DischargeMedicinePdf[],
  showComposition: boolean,
  x: number,
  w: number,
  startY: number,
): Promise<number> {
  const TABLE_FONT = 11;
  const INSTR_FONT = 10;
  const COMP_FONT = 8;
  const COMP_LINE_H = mmToPt(4.5);
  const colSr = mmToPt(8);
  const colDuration = mmToPt(22);
  const cellPad = mmToPt(2);
  const lineH = mmToPt(8.5);
  const COMP_GAP = mmToPt(4);
  const colFreq = mmToPt(38.5);
  const colDrugBase = w - colSr - colFreq - colDuration - mmToPt(31.5);
  const drugShareToInstr = colDrugBase * 0.15;
  const colDrug = colDrugBase - drugShareToInstr;
  const colInstr = mmToPt(31.5) + drugShareToInstr;

  // ASCII fraction conversion (private inlined copy — the 2026-10-02
  // root-cause fix: only emit glyphs jsPDF measures exactly).
  const decimalToFraction = (s: string): string => {
    const n = Number(s);
    if (!Number.isFinite(n) || !s.includes(".")) return s;
    const sign = n < 0 ? "-" : "";
    const abs = Math.abs(n);
    const whole = Math.floor(abs);
    const decimal = abs - whole;
    const asciiFrac: [number, string][] = [
      [0.5, "1/2"],
      [0.25, "1/4"],
      [0.75, "3/4"],
      [0.125, "1/8"],
      [0.375, "3/8"],
      [0.625, "5/8"],
      [0.875, "7/8"],
      [0.333333, "1/3"],
      [0.666667, "2/3"],
      [0.2, "1/5"],
      [0.4, "2/5"],
      [0.6, "3/5"],
      [0.8, "4/5"],
    ];
    for (const [val, label] of asciiFrac) {
      if (Math.abs(decimal - val) < 0.001) {
        if (whole === 0) return `${sign}${label}`;
        return `${sign}${whole} ${label}`;
      }
    }
    return s;
  };

  const fmtFreq = (f: string | null | undefined): string => {
    if (!f) return "";
    return f
      .split(/(\s*-\s*)/)
      .map((seg) => {
        if (/^\s*-\s*$/.test(seg)) return " - ";
        const trimmed = seg.trim();
        if (!trimmed) return seg;
        return decimalToFraction(trimmed);
      })
      .join("");
  };

  const colBoundaries = [
    x + cellPad + colSr,
    x + cellPad + colSr + colDrug,
    x + cellPad + colSr + colDrug + colInstr,
    x + cellPad + colSr + colDrug + colInstr + colFreq,
  ];
  const instrCenterX = x + cellPad + colSr + colDrug + colInstr / 2;
  const freqCenterX = x + cellPad + colSr + colDrug + colInstr + colFreq / 2;

  const drawTableHeader = (headerY: number): number => {
    doc.setFillColor(247, 240, 232);
    doc.rect(x, headerY, w, mmToPt(8), "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(TABLE_FONT);
    doc.setTextColor(124, 45, 18);
    const hSr = x + cellPad;
    const hDrug = x + cellPad + colSr + cellPad;
    const hDays = x + cellPad + colSr + colDrug + colInstr + colFreq;
    doc.text("No.", hSr, headerY + mmToPt(5.5));
    doc.text("Drug", hDrug, headerY + mmToPt(5.5));
    doc.text("Instructions", instrCenterX, headerY + mmToPt(5.5), { align: "center" });
    doc.text("Frequency", freqCenterX, headerY + mmToPt(5.5), { align: "center" });
    doc.text("Days", hDays + cellPad, headerY + mmToPt(5.5));
    doc.setFont("helvetica", "normal");
    doc.setTextColor(26, 20, 19);
    doc.setFontSize(TABLE_FONT);
    return headerY + mmToPt(8);
  };

  const tableTop = startY;
  let y = drawTableHeader(tableTop);
  const pageTopRef = { val: tableTop };
  let rowBoundaries: number[] = [y];

  const drawWrapped = (lines: string[], lx: number, startY2: number) => {
    for (let i = 0; i < lines.length; i++) {
      doc.text(lines[i], lx, startY2 + lineH * i);
    }
  };

  const drawCompLines = (comp: string | null, showComp: boolean, lx: number, lastNameBaselineY: number): number => {
    if (!showComp || !comp || !comp.trim()) return 0;
    doc.setFontSize(COMP_FONT);
    const lines = doc.splitTextToSize(comp.trim(), colDrug - cellPad - mmToPt(1)) as string[];
    const start = lastNameBaselineY + COMP_GAP;
    for (let i = 0; i < lines.length; i++) {
      doc.text(lines[i], lx, start + COMP_LINE_H * i);
    }
    doc.setFontSize(TABLE_FONT);
    return lines.length;
  };

  const drawSeparators = (pageTop: number, endY: number) => {
    // ALL table ruling at the single visible weight (2026-10-26, mirrors
    // the prescription PDF): vertical column separators, the outer frame,
    // and horizontal row dividers — 0.5pt warm gray (150,138,128) so they
    // print cleanly.
    doc.setDrawColor(...RULE_COLOR);
    doc.setLineWidth(RULE_WIDTH);
    for (const bx of colBoundaries) {
      doc.line(bx, pageTop, bx, endY);
    }
    doc.rect(x, pageTop, w, endY - pageTop);
    for (const hy of rowBoundaries) {
      if (hy > pageTop + 0.5 && hy < endY - 0.5) {
        doc.line(x, hy, x + w, hy);
      }
    }
  };

  const onPageBreak = async (prevPageTop: number, endY: number): Promise<{ y: number; pageTop: number }> => {
    drawSeparators(prevPageTop, endY);
    // The new page's frame starts at the header band TOP (pageStart) —
    // matching page 1, where tableTop is captured before drawTableHeader —
    // so the header band is framed and the vertical separators cross it on
    // every page the table spans.
    const pageStart = await addPageWithHeader(doc);
    const headerBottom = drawTableHeader(pageStart);
    rowBoundaries = [headerBottom];
    return { y: headerBottom, pageTop: pageStart };
  };

  const drawMedicineRow = async (m: DischargeMedicinePdf, rowIdx: number, startY2: number): Promise<number> => {
    let yy = startY2;
    const base = mmToPt(6);
    const stepBase = mmToPt(4);
    const stepLineH = mmToPt(7);
    const cxSr = x + cellPad;
    const cxDrug = x + cellPad + colSr + cellPad;
    const cxDays = x + cellPad + colSr + colDrug + colInstr + colFreq;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(TABLE_FONT);

    const drugLines = doc.splitTextToSize(m.name, colDrug - cellPad - mmToPt(1)) as string[];
    doc.setFontSize(COMP_FONT);
    const compLines = (showComposition && m.composition && m.composition.trim())
      ? doc.splitTextToSize(m.composition.trim(), colDrug - cellPad - mmToPt(1)) as string[]
      : [];
    doc.setFontSize(TABLE_FONT);

    const groups = m.taper && m.taper.length > 0 ? groupTaperSteps(m.taper, m.name) : null;
    const multiGroup = !!(groups && groups.length > 1);

    let instrLines: string[];
    if ((!m.taper || m.taper.length === 0) && m.instructions) {
      doc.setFontSize(INSTR_FONT);
      instrLines = doc.splitTextToSize(m.instructions, colInstr - cellPad - mmToPt(1)) as string[];
      doc.setFontSize(TABLE_FONT);
    } else {
      instrLines = ["" as string];
    }

    const freqStr = (!m.taper || m.taper.length === 0) ? fmtFreq(m.frequency) : "";
    const freqLines: string[] = freqStr
      ? doc.splitTextToSize(freqStr, colFreq - cellPad - mmToPt(1)) as string[]
      : ["" as string];

    const compBottomPad = mmToPt(2);
    const nameBlockH = compLines.length > 0
      ? base + lineH * (drugLines.length - 1) + COMP_GAP + COMP_LINE_H * (compLines.length - 1) + compBottomPad
      : lineH;
    const mainH = Math.max(nameBlockH, lineH * Math.max(instrLines.length, freqLines.length, 1));

    let blockH = mainH;
    if (groups) {
      if (multiGroup) {
        blockH = 0;
        for (let gi = 0; gi < groups.length; gi++) {
          const g = groups[gi];
          const gComp = (g.steps.find((s) => s.composition && s.composition.trim())?.composition ?? "").trim() || null;
          const gCompCount = (showComposition && gComp)
            ? (doc.setFontSize(COMP_FONT), doc.splitTextToSize(gComp, colDrug - cellPad - mmToPt(1)) as string[]).length
            : 0;
          if (doc.getFontSize() !== TABLE_FONT) doc.setFontSize(TABLE_FONT);
          const gLinesCount = doc.splitTextToSize(g.name, colDrug - cellPad - mmToPt(1)).length;
          const headerH = gCompCount > 0
            ? base + lineH * (gLinesCount - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + mmToPt(2)
            : Math.max(lineH * gLinesCount, lineH);
          blockH += (gi === 0 ? Math.max(headerH, mainH) : headerH);
          for (const step of g.steps) {
            let sInstr: string[];
            if (step.instructions) {
              doc.setFontSize(INSTR_FONT);
              sInstr = doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[];
              doc.setFontSize(TABLE_FONT);
            } else {
              sInstr = ["" as string];
            }
            const sFreqCount = (doc.splitTextToSize(fmtFreq(step.frequency), colFreq - cellPad - mmToPt(1)) as string[]).length;
            blockH += stepLineH * Math.max(sInstr.length, sFreqCount, 1);
          }
        }
      } else {
        for (const step of groups[0].steps) {
          let sInstr: string[];
          if (step.instructions) {
            doc.setFontSize(INSTR_FONT);
            sInstr = doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[];
            doc.setFontSize(TABLE_FONT);
          } else {
            sInstr = ["" as string];
          }
          const sFreqCount = (doc.splitTextToSize(fmtFreq(step.frequency), colFreq - cellPad - mmToPt(1)) as string[]).length;
          blockH += stepLineH * Math.max(sInstr.length, sFreqCount, 1);
        }
      }
    }

    const region = getTextRegion(doc);
    const bottomLimit = region.y + region.maxHeight;
    if (yy + blockH > bottomLimit) {
      const { y: newY, pageTop } = await onPageBreak(pageTopRef.val, yy);
      yy = newY;
      pageTopRef.val = pageTop;
    }
    if (rowIdx % 2 === 1) {
      doc.setFillColor(252, 248, 243);
      doc.rect(x, yy, w, blockH, "F");
    }

    doc.setTextColor(26, 20, 19);
    doc.text(String(m.serialNo), cxSr, yy + base);

    if (!m.taper || m.taper.length === 0) {
      doc.text(drugLines[0], cxDrug, yy + base);
      if (drugLines.length > 1) {
        drawWrapped(drugLines.slice(1), cxDrug, yy + base + lineH);
      }
      if (compLines.length > 0) {
        drawCompLines(m.composition, showComposition, cxDrug, yy + base + lineH * (drugLines.length - 1));
      }
      if (m.instructions) {
        doc.setFontSize(INSTR_FONT);
        for (let i = 0; i < instrLines.length; i++) {
          doc.text(instrLines[i], instrCenterX, yy + base + lineH * i, { align: "center" });
        }
        doc.setFontSize(TABLE_FONT);
      }
      if (freqLines.length > 0) {
        for (let i = 0; i < freqLines.length; i++) {
          doc.text(freqLines[i], freqCenterX, yy + base + lineH * i, { align: "center" });
        }
      }
      doc.text(m.duration ?? "", cxDays + cellPad, yy + base);
      yy += mainH;
      return yy;
    }

    if (!groups) { yy += mainH; return yy; }

    const tabIndent = mmToPt(6);
    if (multiGroup) {
      for (let gi = 0; gi < groups.length; gi++) {
        const g = groups[gi];
        const gName = g.name;
        const gLines = doc.splitTextToSize(gName, colDrug - cellPad - mmToPt(1)) as string[];
        const gComp = (g.steps.find((s) => s.composition && s.composition.trim())?.composition ?? "").trim() || null;
        const gCompCount = (showComposition && gComp)
          ? (doc.setFontSize(COMP_FONT), (doc.splitTextToSize(gComp, colDrug - cellPad - mmToPt(1)) as string[]).length)
          : 0;
        if (doc.getFontSize() !== TABLE_FONT) doc.setFontSize(TABLE_FONT);
        const gHeaderH = gCompCount > 0
          ? base + lineH * (gLines.length - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + mmToPt(2)
          : Math.max(lineH * gLines.length, lineH);
        if (gi === 0) {
          doc.text(gLines[0], cxDrug, yy + base);
          if (gLines.length > 1) {
            drawWrapped(gLines.slice(1), cxDrug, yy + base + lineH);
          }
          if (gCompCount > 0) {
            drawCompLines(gComp, showComposition, cxDrug, yy + base + lineH * (gLines.length - 1));
          }
          yy += Math.max(gHeaderH, mainH);
        } else {
          const headerH = gCompCount > 0
            ? base + lineH * (gLines.length - 1) + COMP_GAP + COMP_LINE_H * (gCompCount - 1) + mmToPt(2)
            : Math.max(lineH * gLines.length, lineH);
          if (yy + headerH > bottomLimit) {
            const { y: newY, pageTop } = await onPageBreak(pageTopRef.val, yy);
            yy = newY;
            pageTopRef.val = pageTop;
          }
          doc.text(gLines[0], cxDrug, yy + base);
          if (gLines.length > 1) {
            drawWrapped(gLines.slice(1), cxDrug, yy + base + lineH);
          }
          if (gCompCount > 0) {
            drawCompLines(gComp, showComposition, cxDrug, yy + base + lineH * (gLines.length - 1));
          }
          yy += headerH;
        }
        for (const step of g.steps) {
          const [fy, fmo, fd] = step.fromDate.split("-").map(Number);
          const toIso = new Date(Date.UTC(fy, fmo - 1, fd + step.days - 1)).toISOString().slice(0, 10);
          const rangeText = step.days === 1
            ? fmtDatePdf(step.fromDate)
            : `${fmtDatePdf(step.fromDate)} – ${fmtDatePdf(toIso)}`;
          let sInstr: string[];
          if (step.instructions) {
            doc.setFontSize(INSTR_FONT);
            sInstr = doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[];
          } else {
            sInstr = ["" as string];
          }
          const sFreqLines = doc.splitTextToSize(fmtFreq(step.frequency), colFreq - cellPad - mmToPt(1)) as string[];
          const stepH = stepLineH * Math.max(sInstr.length, sFreqLines.length, 1);
          if (yy + stepH > bottomLimit) {
            const { y: newY, pageTop } = await onPageBreak(pageTopRef.val, yy);
            yy = newY;
            pageTopRef.val = pageTop;
          }
          doc.setTextColor(60, 50, 45);
          doc.text(rangeText, cxDrug + tabIndent, yy + stepBase);
          if (step.instructions) {
            for (let i = 0; i < sInstr.length; i++) {
              doc.text(sInstr[i], instrCenterX, yy + stepBase + stepLineH * i, { align: "center" });
            }
          }
          doc.setFontSize(TABLE_FONT);
          doc.setTextColor(26, 20, 19);
          for (let i = 0; i < sFreqLines.length; i++) {
            doc.text(sFreqLines[i], freqCenterX, yy + stepBase + stepLineH * i, { align: "center" });
          }
          doc.text(`${step.days} days`, cxDays + cellPad, yy + stepBase);
          doc.setTextColor(26, 20, 19);
          yy += stepH;
        }
      }
      return yy;
    }

    // Single-group taper.
    doc.text(drugLines[0], cxDrug, yy + base);
    if (drugLines.length > 1) {
      drawWrapped(drugLines.slice(1), cxDrug, yy + base + lineH);
    }
    if (compLines.length > 0) {
      drawCompLines(m.composition, showComposition, cxDrug, yy + base + lineH * (drugLines.length - 1));
    }
    yy += mainH;
    for (const step of groups[0].steps) {
      const [fy, fmo, fd] = step.fromDate.split("-").map(Number);
      const toIso = new Date(Date.UTC(fy, fmo - 1, fd + step.days - 1)).toISOString().slice(0, 10);
      const rangeText = step.days === 1
        ? fmtDatePdf(step.fromDate)
        : `${fmtDatePdf(step.fromDate)} – ${fmtDatePdf(toIso)}`;
      let sInstr: string[];
      if (step.instructions) {
        doc.setFontSize(INSTR_FONT);
        sInstr = doc.splitTextToSize(step.instructions, colInstr - cellPad - mmToPt(1)) as string[];
      } else {
        sInstr = ["" as string];
      }
      const sFreqLines = doc.splitTextToSize(fmtFreq(step.frequency), colFreq - cellPad - mmToPt(1)) as string[];
      const stepH = stepLineH * Math.max(sInstr.length, sFreqLines.length, 1);
      if (yy + stepH > bottomLimit) {
        const { y: newY, pageTop } = await onPageBreak(pageTopRef.val, yy);
        yy = newY;
        pageTopRef.val = pageTop;
      }
      doc.setTextColor(60, 50, 45);
      doc.text(rangeText, cxDrug + tabIndent, yy + stepBase);
      if (step.instructions) {
        for (let i = 0; i < sInstr.length; i++) {
          doc.text(sInstr[i], instrCenterX, yy + stepBase + stepLineH * i, { align: "center" });
        }
      }
      doc.setFontSize(TABLE_FONT);
      doc.setTextColor(26, 20, 19);
      for (let i = 0; i < sFreqLines.length; i++) {
        doc.text(sFreqLines[i], freqCenterX, yy + stepBase + stepLineH * i, { align: "center" });
      }
      doc.text(`${step.days} days`, cxDays + cellPad, yy + stepBase);
      yy += stepH;
    }

    return yy;
  };

  for (let i = 0; i < medicines.length; i++) {
    y = await drawMedicineRow(medicines[i], i, y);
    rowBoundaries.push(y);
  }
  drawSeparators(pageTopRef.val, y);
  y += lineH;

  return y;
}
